import React, { useEffect, useRef, useState } from 'react';
import { CardSection, ConfirmationModal, useModal } from '@europrocurement/flexy-components';
import { DOC_TYPE_REQUIRED } from '@europrocurement/l2d-domain';
import { useTheme } from '@mui/material';
import { useDossierDocumentsCtx } from '@europrocurement/l2d-modules/providers/DossierDocumentsProvider';
import { useDossierDocumentSignContext, useSelectedDossierContext } from '../../../providers';
import RequiredDocumentsHeading from '../RequiredDocumentsHeading/RequiredDocumentsHeading';
import RequiredDocumentsList from '../RequiredDocumentsList/RequiredDocumentsList';
import RequiredDocumentUploader from '../RequiredDocumentUploader/RequiredDocumentUploader';
import useDossierFile from '../../../hooks/useDossierFile';
import { DisplayedDocument } from '../../../providers/types';
import { useDocumentSign, useDocumentSignRender, useTransition } from '../../../hooks';

export interface RequiredDocumentsTabPros extends React.PropsWithChildren {
    source?: 'BACK' | 'FRONT';
}
const RequiredDocumentsTab: React.FunctionComponent<RequiredDocumentsTabPros> = function ({
    children,
    source = 'BACK',
}) {
    const [listItems, setListItems] = useState<Array<DisplayedDocument>>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const { dosDocs, getMissingDocumentsCountByCategory, getDossierDocumentsByCategory } =
        useDossierDocumentsCtx();

    const { palette } = useTheme();
    const { dossier, refreshDossier } = useSelectedDossierContext();
    const { canApplyTransition, applyTransition } = useTransition();
    const { modalActions } = useModal();
    const requiredMissingDocumentsCount = useRef(-1);
    const {
        handleAddFile,
        handleRemoveFile,
        handleRemoveDocument,
        handleShow,
        handleDownload,
        handleAdditionalUploads,
        handleMoveFile,
        numberFilesChanged,
    } = useDossierFile({
        setIsSubmitting,
        typeDoc: DOC_TYPE_REQUIRED,
        source,
    });

    const { showSignedDocument } = useDocumentSignRender();
    const { handleSignSingleDocument } = useDocumentSign();
    const { getDossiersBySignedFileId } = useDossierDocumentSignContext();

    const handleSignSingleDocProxy = (label: string, fichierId: number, fileName?: string) => {
        handleSignSingleDocument(
            dossier.id as number,
            dossier.prescripteurId as number,
            dossier.prescripteurLibelle as string,
            dossier.prescripteurEmail as string,
            label,
            fichierId,
            fileName,
        );
    };

    const handleDownloadSigned = (fichierId: number, fileName: string) => {
        showSignedDocument(fichierId, fileName, getDossiersBySignedFileId(fichierId));
    };

    useEffect(() => {
        const mergedDocuments = [
            ...getDossierDocumentsByCategory({ categoryId: DOC_TYPE_REQUIRED }),
            ...getDossierDocumentsByCategory({
                categoryId: DOC_TYPE_REQUIRED,
                additionalDocs: true,
            }),
        ];
        if (source === 'FRONT') {
            // Remove available actions on documents card for the front
            setListItems(
                mergedDocuments.map((document) => ({
                    ...document,
                    movable: false,
                    removableDoc: false,
                })),
            );
        } else {
            setListItems(mergedDocuments);
        }

        requiredMissingDocumentsCount.current = getMissingDocumentsCountByCategory({
            categoryId: DOC_TYPE_REQUIRED,
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dosDocs, getDossiersBySignedFileId]);

    const countSuccessfull = listItems.reduce(
        (accumulator, doc) =>
            doc.status === 'success' && doc.hasType ? accumulator + 1 : accumulator,
        0,
    );

    const countTotal = listItems.reduce(
        (accumulator, doc) => (doc.hasType ? accumulator + 1 : accumulator),
        0,
    );

    const handleUpdateStatusDossierToCheck = async () => {
        if (canApplyTransition(dossier, 'to-check') && dossier.stepId === 3) {
            await applyTransition(dossier, 'to-check');
            modalActions.close();
            refreshDossier();
        }
    };

    // Check if some files were updated and if the dossier status has a specific control
    // /!\ Do not work locally with a yarn not build (dbl render)
    useEffect(
        () => () => {
            if (
                source === 'FRONT' &&
                canApplyTransition(dossier, 'to-check') &&
                dossier.stepId === 3
            ) {
                if (numberFilesChanged.current > 0 && requiredMissingDocumentsCount.current === 0) {
                    modalActions.call(
                        <ConfirmationModal
                            messages={{
                                question: 'Confirmation ?',
                                detail: {
                                    message:
                                        'Vous avez modifié des documents, souhaitez-vous passer le dossier en A contrôler ?',
                                    severity: 'info',
                                },
                                validation: 'Oui, j’ai terminé',
                                annulation: 'Non, je continue',
                            }}
                            actionOnValidation={handleUpdateStatusDossierToCheck}
                            actionOnCancellation={() => {
                                modalActions.close();
                            }}
                            color={palette.primary}
                        />,
                    );
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    return (
        <CardSection
            header={
                <RequiredDocumentsHeading
                    currentCount={countSuccessfull}
                    totalCount={countTotal}
                />
            }
            sxContent={{
                padding: '0 32px 32px',
                display: 'flex',
                flexDirection: 'column',
                gap: '24px 0',
            }}
        >
            <RequiredDocumentsList
                documents={listItems}
                onUpload={handleAddFile}
                onDeletion={handleRemoveFile}
                onDocDeletion={handleRemoveDocument}
                onDownload={handleDownload}
                onShow={handleShow}
                onSign={handleSignSingleDocProxy}
                onDownloadSigned={handleDownloadSigned}
                onMove={handleMoveFile}
            />
            <RequiredDocumentUploader
                uploading={isSubmitting}
                onChange={handleAdditionalUploads}
            />
            {children}
        </CardSection>
    );
};

export default RequiredDocumentsTab;
